import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, ArrowDownRight, ArrowUpRight, Pencil } from "lucide-react";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);

const blank = () => ({ date: todayStr(), kind: "deposit", amount: "", description: "", category: "", reference: "" });

const CATEGORY_SUGGESTIONS = [
    "Software", "Travel", "Office Supplies", "Utilities", "Rent", "Bank Fees",
    "Owner Contribution", "Owner Drawings", "Transfer", "Refund", "Sales", "Interest",
];

/**
 * AddBankTxnDialog — manual create OR edit of a bank transaction.
 *
 * Props:
 *   open               — boolean
 *   onOpenChange(v)    — close handler
 *   account            — { id, name, currency } currently selected account
 *   editing            — existing txn doc when editing, else null
 *   onSaved()          — fired on successful save so the parent can reload
 */
export default function AddBankTxnDialog({ open, onOpenChange, account, editing, onSaved }) {
    const [form, setForm] = useState(blank());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            // Bank txns store positive=inflow, negative=outflow. The dialog
            // models the same as a kind toggle + always-positive amount input
            // so the user doesn't have to think about signs.
            const amt = Number(editing.amount) || 0;
            setForm({
                date: (editing.date || todayStr()).slice(0, 10),
                kind: amt < 0 ? "withdrawal" : "deposit",
                amount: String(Math.abs(amt)),
                description: editing.description || "",
                category: editing.category || "",
                reference: editing.reference || "",
            });
        } else {
            setForm({ ...blank(), date: todayStr() });
        }
    }, [open, editing]);

    const submit = async () => {
        const amt = parseFloat(form.amount);
        if (!form.date || Number.isNaN(amt) || amt <= 0) {
            toast.error("Enter a valid date and a positive amount.");
            return;
        }
        if (!form.description.trim()) {
            toast.error("Add a short description so the entry is searchable later.");
            return;
        }
        if (!account?.id) {
            toast.error("Pick a bank account first.");
            return;
        }
        const signed = form.kind === "withdrawal" ? -Math.abs(amt) : Math.abs(amt);
        const payload = {
            bank_account_id: account.id,
            date: form.date,
            amount: signed,
            description: form.description.trim(),
            category: form.category.trim() || null,
            reference: form.reference.trim() || null,
        };
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/bank-transactions/${editing.id}`, payload);
                toast.success("Transaction updated");
            } else {
                await api.post("/bank-transactions", payload);
                toast.success("Transaction added");
            }
            onOpenChange?.(false);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange?.(v); }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl flex items-center gap-2">
                        {editing ? (<><Pencil className="h-4 w-4 text-secondary" /> Edit transaction</>)
                                 : (<><Plus className="h-4 w-4 text-secondary" /> Add transaction</>)}
                    </DialogTitle>
                </DialogHeader>
                {account && (
                    <div className="space-y-3">
                        <div className="rounded-2xl bg-sand-100 p-3 text-xs">
                            <div className="text-muted-foreground uppercase tracking-wider">Account</div>
                            <div className="font-medium text-ocean-700 mt-0.5">{account.name} · {account.currency}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Date</Label>
                                <Input type="date" value={form.date}
                                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                                    className="mt-1.5" data-testid="bank-txn-date" />
                            </div>
                            <div>
                                <Label>Type</Label>
                                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="bank-txn-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="deposit">
                                            <span className="inline-flex items-center gap-1.5">
                                                <ArrowDownRight className="h-3.5 w-3.5 text-secondary-dark" /> Deposit (money in)
                                            </span>
                                        </SelectItem>
                                        <SelectItem value="withdrawal">
                                            <span className="inline-flex items-center gap-1.5">
                                                <ArrowUpRight className="h-3.5 w-3.5 text-coral-dark" /> Withdrawal (money out)
                                            </span>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Amount</Label>
                            <Input type="number" step="0.01" min="0" value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                className="mt-1.5" placeholder="0.00" data-testid="bank-txn-amount" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                className="mt-1.5"
                                placeholder="e.g. ATM withdrawal, owner contribution, bank fee"
                                data-testid="bank-txn-description" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Category <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                <Input value={form.category}
                                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                                    className="mt-1.5"
                                    placeholder="Auto-detected if blank"
                                    list="bank-txn-cat-suggestions"
                                    data-testid="bank-txn-category" />
                                <datalist id="bank-txn-cat-suggestions">
                                    {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                                </datalist>
                            </div>
                            <div>
                                <Label>Reference <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                <Input value={form.reference}
                                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                    className="mt-1.5"
                                    placeholder="Check #, confirmation #"
                                    data-testid="bank-txn-reference" />
                            </div>
                        </div>
                        {form.amount && (
                            <div className={`rounded-2xl px-3 py-2 text-xs ${form.kind === "withdrawal" ? "bg-coral/10 text-coral-dark" : "bg-secondary/10 text-secondary-dark"}`} data-testid="bank-txn-preview">
                                Preview: <span className="font-mono font-semibold">
                                    {form.kind === "withdrawal" ? "−" : "+"}
                                    {fmtMoney(Math.abs(parseFloat(form.amount) || 0), account.currency)}
                                </span> on {fmtDate(form.date)}
                            </div>
                        )}
                        {editing?.reconciled && (
                            <div className="rounded-2xl bg-secondary/5 border border-secondary/30 px-3 py-2 text-xs text-secondary-dark">
                                Heads up — this transaction was already reconciled. Saving will un-reconcile it so you can re-confirm the match.
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="bg-ocean-700 text-white" data-testid="bank-txn-submit">
                        {saving ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>)
                                : (editing ? "Save changes" : "Add transaction")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
